import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollReveal } from '../components/ScrollReveal';

// Image URLs
const IMAGES = {
  classroom: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/rqxgj890_IMG_0358.JPG",
  agriculture: "https://customer-assets.emergentagent.com/job_roots-32-learners/artifacts/7qgtc9xr_IMG_0177%203.jpg",
};

const admissionsProcess = [
  {
    step: 1,
    title: 'Information Session',
    description: 'Invitation-only session to understand the Nova Roots model and approach.',
  },
  {
    step: 2,
    title: 'Learner Observation Morning',
    description: 'Structured observation session where your child engages with the learning environment.',
  },
  {
    step: 3,
    title: 'Founder Conversation',
    description: 'Direct conversation with the Founder to ensure alignment on values and expectations.',
  },
  {
    step: 4,
    title: 'Offer & Deposit',
    description: 'Receive your offer and secure your place in the Founding Cohort.',
  },
];

export default function Admissions() {
  return (
    <div data-testid="admissions-page" className="page-transition pt-24">
      {/* Hero Section */}
      <section className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-3xl">
            <ScrollReveal>
              <p className="text-sm font-medium uppercase tracking-wider text-[#C27856] mb-4">
                Admissions
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A3C34] leading-[1.1]">
                Join the Founding Cohort
              </h1>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="mt-12 flex flex-wrap gap-12">
                <div>
                  <p className="text-sm font-medium uppercase tracking-wider text-[#5C6B6B]">Ages</p>
                  <p className="text-2xl font-heading font-light text-[#1A3C34] mt-2">4–10</p>
                </div>
                <div>
                  <p className="text-sm font-medium uppercase tracking-wider text-[#5C6B6B]">Capacity</p>
                  <p className="text-2xl font-heading font-light text-[#1A3C34] mt-2">32 learners</p>
                </div>
                <div>
                  <p className="text-sm font-medium uppercase tracking-wider text-[#5C6B6B]">Opening</p>
                  <p className="text-2xl font-heading font-light text-[#1A3C34] mt-2">September 2026</p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process-section" className="py-28 md:py-36 bg-[#F2F0E9]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            <div>
              <ScrollReveal>
                <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                  Process
                </h2>
              </ScrollReveal>
              
              <div className="mt-12 space-y-8">
                {admissionsProcess.map((item, index) => (
                  <ScrollReveal key={item.step} delay={index * 100}>
                    <div className="timeline-item pb-8">
                      <div className="flex items-start gap-4">
                        <span className="text-sm font-medium text-[#C27856]">
                          {String(item.step).padStart(2, '0')}
                        </span>
                        <div>
                          <h3 className="font-heading text-lg font-medium text-[#1A3C34]">
                            {item.title}
                          </h3>
                          <p className="mt-1 text-[#5C6B6B] font-light text-sm">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
            
            <ScrollReveal delay={150}>
              <div className="img-hover-zoom rounded-2xl overflow-hidden sticky top-32">
                <img 
                  src={IMAGES.classroom}
                  alt="Nova Roots classroom"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Fees Section */}
      <section data-testid="fees-section" className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-2xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] text-center">
                Fees
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <div className="mt-16 bg-white rounded-2xl border border-[#EBE8E0] overflow-hidden">
                <div className="p-8 bg-[#1A3C34]">
                  <h3 className="font-heading text-sm font-medium uppercase tracking-wider text-[#F9F8F4]/70">
                    Deposit
                  </h3>
                  <p className="text-3xl font-heading font-light text-[#F9F8F4] mt-2">
                    KES 200,000
                  </p>
                  <p className="text-sm text-[#F9F8F4]/60 mt-2">
                    Non-refundable. Refundable only if school does not open.
                  </p>
                </div>
                
                <div className="p-8">
                  <h4 className="font-heading text-sm font-medium uppercase tracking-wider text-[#5C6B6B] mb-6">
                    Annual Fees
                  </h4>
                  <table className="fee-table">
                    <thead>
                      <tr>
                        <th>Pod</th>
                        <th>Ages</th>
                        <th>Annual</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="font-medium text-[#1A3C34]">Pod 1</td>
                        <td className="text-[#5C6B6B]">4–7</td>
                        <td className="font-medium text-[#1A3C34]">KES 1,250,000</td>
                      </tr>
                      <tr>
                        <td className="font-medium text-[#1A3C34]">Pod 2</td>
                        <td className="text-[#5C6B6B]">8–10</td>
                        <td className="font-medium text-[#1A3C34]">KES 1,450,000</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="p-8 bg-[#F2F0E9] border-t border-[#EBE8E0]">
                  <h4 className="font-heading text-sm font-medium uppercase tracking-wider text-[#5C6B6B] mb-4">
                    Payment
                  </h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-3 text-[#2D3A3A]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C27856] mt-1.5 flex-shrink-0" />
                      <span className="font-light">Annual upfront (2% administrative credit)</span>
                    </li>
                    <li className="flex items-start gap-3 text-[#2D3A3A]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C27856] mt-1.5 flex-shrink-0" />
                      <span className="font-light">Termly instalments</span>
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <p className="mt-10 text-center text-[#5C6B6B] font-light text-sm">
                Nova Roots may not suit families seeking large-scale environments or exam-ranking systems. Limited places available.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Founding Cohort Section */}
      <section data-testid="founding-cohort-section" className="py-28 md:py-36 bg-[#F2F0E9]">
        <div className="container-nova">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <ScrollReveal>
              <div className="img-hover-zoom rounded-2xl overflow-hidden">
                <img 
                  src={IMAGES.agriculture}
                  alt="Agricultural activities"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </ScrollReveal>
            
            <div>
              <ScrollReveal>
                <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34]">
                  Founding Cohort
                </h2>
              </ScrollReveal>
              
              <ScrollReveal delay={100}>
                <p className="mt-8 text-base font-light leading-relaxed text-[#2D3A3A]">
                  The first 32 learners form the Founding Cohort of Nova Roots.
                </p>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <p className="mt-6 text-sm font-light leading-relaxed text-[#5C6B6B]">
                  Founding families participate in a structured partnership model. This group shapes the early culture and rhythm of the school.
                </p>
              </ScrollReveal>
              
              <ScrollReveal delay={300}>
                <p className="mt-8 text-sm text-[#5C6B6B]">
                  Once capacity is reached, aligned families may join the Founding Cohort Waitlist.
                </p>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* Pilot Success Section */}
      <section className="py-28 md:py-36 bg-[#F9F8F4]">
        <div className="container-nova">
          <div className="max-w-2xl mx-auto">
            <ScrollReveal>
              <h2 className="font-serif text-3xl md:text-4xl font-normal text-[#1A3C34] text-center">
                Pilot Success Indicators
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <div className="mt-16 space-y-4">
                {[
                  'Full enrolment',
                  'Measurable literacy and numeracy progression',
                  'Strong learner engagement',
                  'Sustainable unit economics',
                  'Replicable systems'
                ].map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-4 py-3 border-b border-[#1A3C34]/10 last:border-b-0"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C27856] flex-shrink-0" />
                    <span className="text-[#1A3C34] font-light">{item}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#1A3C34]">
        <div className="container-nova text-center">
          <ScrollReveal>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-[#F9F8F4] max-w-xl mx-auto">
              Enquire
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={150}>
            <p className="mt-6 text-base font-light text-[#F9F8F4]/70 max-w-md mx-auto">
              Invitation-only sessions. Prospectus shared post-session.
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={250}>
            <Link 
              to="/enquire" 
              className="mt-10 inline-block bg-[#F9F8F4] text-[#1A3C34] hover:bg-white px-10 py-4 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
              data-testid="enquire-cta-btn"
            >
              Submit Enquiry
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
